/* ==========================================================================
   collection.js — Storefront Filtering enhancement (SPEC §8.5, §14.3, §23.3).
   The filter form is a native GET form (SEO-safe URLs, works pre-JS);
   this module upgrades it: change → fetch section render → swap grid,
   chips and count, update the URL. Grid dims to 0.5 during load (content
   stays visible — no skeleton flash); incoming cards fade in 150 ms.
   ========================================================================== */

function sectionRoot() {
  return document.querySelector('[data-filter-form]')?.closest('.shopify-section');
}

function buildUrl(form) {
  const params = new URLSearchParams(new FormData(form));
  // Drop empty price bounds so URLs stay clean
  for (const [key, value] of [...params.entries()]) {
    if (value === '') params.delete(key);
  }
  return `${window.location.pathname}?${params.toString()}`;
}

let controller;

async function applyFilters(url, { pushState = true } = {}) {
  const root = sectionRoot();
  if (!root) return;
  const sectionId = root.id.replace('shopify-section-', '');
  const grid = root.querySelector('[data-collection-grid]');
  grid?.classList.add('is-loading');

  controller?.abort();
  controller = new AbortController();

  try {
    const sep = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${sep}section_id=${sectionId}`, {
      signal: controller.signal,
    });
    const html = await response.text();
    const incoming = new DOMParser().parseFromString(html, 'text/html');

    ['[data-collection-grid]', '[data-active-chips]', '[data-collection-count]', '.pagination'].forEach(
      (selector) => {
        const current = root.querySelector(selector);
        const next = incoming.querySelector(selector);
        if (current && next) current.replaceWith(next);
        else if (current && !next) current.remove();
        else if (!current && next) {
          // chips bar may (re)appear — anchor it inside the toolbar
          if (selector === '[data-active-chips]') {
            root.querySelector('.collection__toolbar')?.append(next);
          }
        }
      }
    );

    // Apply-button count stays honest
    const applyBtn = root.querySelector('.collection__apply .btn');
    const incomingBtn = incoming.querySelector('.collection__apply .btn');
    if (applyBtn && incomingBtn) applyBtn.textContent = incomingBtn.textContent;

    // Incoming cards fade in 150 ms (§14.3) unless reduced motion
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const newGrid = root.querySelector('[data-collection-grid]');
      if (newGrid) {
        newGrid.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'ease-out' });
      }
    }

    if (pushState) history.pushState({ tjpFilters: true }, '', url);
  } catch (err) {
    if (err.name !== 'AbortError') window.location.assign(url); // degrade to full navigation
  } finally {
    sectionRoot()?.querySelector('[data-collection-grid]')?.classList.remove('is-loading');
  }
}

document.addEventListener('change', (e) => {
  const form = e.target.closest('[data-filter-form]');
  if (!form) return;
  applyFilters(buildUrl(form));
});

document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-filter-form]');
  if (!form) return;
  e.preventDefault();
  applyFilters(buildUrl(form));
  form.closest('tjp-drawer')?.close?.();
});

/* Chip removals + clear-all are plain links — intercept for the same
   section-render path, keep native behavior as fallback */
document.addEventListener('click', (e) => {
  const chip = e.target.closest('[data-active-chips] a.chip');
  if (!chip) return;
  e.preventDefault();
  applyFilters(chip.getAttribute('href'));
});

window.addEventListener('popstate', () => {
  applyFilters(window.location.pathname + window.location.search, { pushState: false });
});
